import { Scenario } from '@scenario/models/scenario.model';
import { ChartMarkerConfiguration } from './chart-marker.configuration';
import { ChartMarkers } from '@scenario/enums/chart/chart-markers.enum';
import {
  chartReferential,
  defaultReferentialValues,
} from '@configurations/scenario/chart/chart-customization-referential.configuration';
import { ChartShape } from '@scenario/enums/chart/chart-shape.enum';
import { chartShapes, dotSvgText } from '@configurations/scenario/chart/shapes';

export class ChartCustomizer {

  constructor(
    public readonly chartMarkerConfiguration: ChartMarkerConfiguration,
    public readonly referential = chartReferential,
    public readonly defaultValues = defaultReferentialValues) {
  }

  retrieveColor(scenario: Scenario) {
    if(this.chartMarkerConfiguration.zone === ChartMarkers.COLOR) {
      return this.referential[scenario.zoneAlias]?.color ?? this.defaultValues.color;
    }
    if(this.chartMarkerConfiguration.source === ChartMarkers.COLOR) {
      return this.referential[scenario.source]?.color ?? this.defaultValues.color;
    }
    if(this.chartMarkerConfiguration.scenario === ChartMarkers.COLOR) {
      return this.referential[scenario.basecase]?.color ?? this.defaultValues.color;
    }
    return this.defaultValues.color;
  }

  retrievePointShape(scenario: Scenario, color: string) {
    let shape: string = ChartShape.DOT;

    if(this.chartMarkerConfiguration.zone === ChartMarkers.SHAPE) {
      shape = this.referential[scenario.zoneAlias]?.shape ?? this.defaultValues.shape;
    }
    if(this.chartMarkerConfiguration.source === ChartMarkers.SHAPE) {
      shape = this.referential[scenario.source]?.shape ?? this.defaultValues.shape;
    }
    if(this.chartMarkerConfiguration.scenario === ChartMarkers.SHAPE) {
      shape = this.referential[scenario.basecase]?.shape ?? this.defaultValues.shape;
    }
    return this.loadSvgAsImageWithColor(shape, color);
  }

  retrieveLineType(scenario: Scenario) {
    if(this.chartMarkerConfiguration.zone === ChartMarkers.LINE_TYPE) {
      return this.referential[scenario.zoneAlias]?.line ?? this.defaultValues.line;
    }
    if(this.chartMarkerConfiguration.source === ChartMarkers.LINE_TYPE) {
      return this.referential[scenario.source]?.line ?? this.defaultValues.line;
    }
    if(this.chartMarkerConfiguration.scenario === ChartMarkers.LINE_TYPE) {
      return this.referential[scenario.basecase]?.line ?? this.defaultValues.line;
    }
    return this.defaultValues.line;
  }

  retrieveThickness(scenario : Scenario) {
    if(this.chartMarkerConfiguration.zone === ChartMarkers.THICKNESS) {
      return this.referential[scenario.zoneAlias]?.thickness ?? this.defaultValues.thickness;
    }
    if(this.chartMarkerConfiguration.source === ChartMarkers.THICKNESS) {
      return this.referential[scenario.source]?.thickness ?? this.defaultValues.thickness;
    }
    if(this.chartMarkerConfiguration.scenario === ChartMarkers.THICKNESS) {
      return this.referential[scenario.basecase]?.thickness ?? this.defaultValues.thickness;
    }
    if(this.chartMarkerConfiguration.publicationDate === ChartMarkers.THICKNESS) {
      return scenario.thickness ?? this.defaultValues.thickness;
    }
    return this.defaultValues.thickness;
  }

  private readonly imageCache = new Map<string, HTMLImageElement>();

  loadSvgAsImageWithColor = (shape: string, fillColor: string): HTMLImageElement => {
    const cacheKey = `${shape}::${fillColor}`;
    const cached = this.imageCache.get(cacheKey);
    if (cached) {
      return cached;
    }

    let svgText: string = chartShapes[shape as ChartShape] ?? dotSvgText;
    svgText = svgText.replaceAll(/fill="[^"]*"/g, `fill="${fillColor}"`);

    const svgBlob = new Blob([svgText], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(svgBlob);

    const image = new Image();
    image.src = url;
    image.onload = () => URL.revokeObjectURL(url);

    this.imageCache.set(cacheKey, image);
    return image;
  };

  processScenariosThickness(scenarios: Scenario[]) {
    const thicknesses = [
      this.referential['Last Release'].thickness,
      this.referential['2 Last Releases'].thickness,
      this.referential['Range'].thickness,
      0.75
    ];

    const groupedBySource = Object.groupBy(scenarios, (scenario: Scenario) => scenario.source);

    return Object.keys(groupedBySource).flatMap(source => {
      const scenariosBySource = groupedBySource[source] || [];
      const groupedByDate = Object.groupBy(scenariosBySource, (scenario: Scenario) => scenario.unifiedPublicationDate);

      return Object.values(groupedByDate).flatMap((scenarios, index) => {
        const thickness = index > 2 ? thicknesses[3] : thicknesses[index];
        return (scenarios || []).map(scenario => ({ ...scenario, thickness }));
      });
    });
  }
}
